import { Component, OnInit, TemplateRef, computed, inject, signal, viewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { PrintPdfFilesService } from './print-pdf-files.service';
import { FenceService } from 'src/app/services/fence.service';
import {
  PaddockReportingTarget,
  PropertyReportingTarget,
  ReportingService
} from 'src/app/services/reporting.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FarmWoodlotToolWelcomeMessageComponent } from './farm-woodlot-tool-welcome-message/farm-woodlot-tool-welcome-message.component';
import { CommonModule } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MaterialsSpeciesPlantingComponent } from './materials-species-planting/materials-species-planting.component';
import { TreeProtectionComponent } from './tree-protection/tree-protection.component';
import { SitePreparationGuidelinesComponent } from './site-preparation-guidelines/site-preparation-guidelines.component';
import { IndicativeYieldsComponent } from './indicative-yields/indicative-yields.component';
import { MarketGuidelinesComponent } from './market-guidelines/market-guidelines.component';
import { PtrGuidelinesComponent } from './ptr-guidelines/ptr-guidelines.component';
import { DatePipe } from '@angular/common';
import { generateFarmWoodlotPDF } from './utils';
import { NgpExitAnimationManager } from 'ng-primitives/internal';
import { NgpDialogManager } from 'ng-primitives/dialog';

import {
  NgpDialog,
  NgpDialogTitle,
  NgpDialogDescription,
  NgpDialogTrigger,
  NgpDialogOverlay,
} from 'ng-primitives/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { AccordionComponent, AccordionItemComponent, DialogComponent } from '@aetherion/ui';

interface StageCompletedObject {
  [key: number]: {
    completed: boolean;
    data: any;
  };
}

@Component({
  selector: 'app-farm-woodlot-tool',
  templateUrl: './farm-woodlot-tool.component.html',
  styleUrls: ['./farm-woodlot-tool.component.css'],
  standalone: true,
  providers:[NgpExitAnimationManager],
  imports: [
    CommonModule,
    RouterModule,
    MatDialogModule,
    MatToolbarModule,
    DatePipe,
    MaterialsSpeciesPlantingComponent,
    TreeProtectionComponent,
    SitePreparationGuidelinesComponent,
    IndicativeYieldsComponent,
    MarketGuidelinesComponent,
    PtrGuidelinesComponent,
    AccordionComponent,
    AccordionItemComponent,
    DialogComponent,
     NgpDialog,
  NgpDialogTitle,
  NgpDialogDescription,
  NgpDialogTrigger,
  NgpDialogOverlay,
  ]
})
export class FarmWoodlotToolComponent implements OnInit {
  private reportingService = inject(ReportingService);
  private fenceService = inject(FenceService);
  printPDF = inject(PrintPdfFilesService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private dialogManager = inject(NgpDialogManager);
  private sanitizer = inject(DomSanitizer);

  private dialogRef = viewChild<TemplateRef<any>>('dialog');

  private activePropertySub$: Subscription;
  reportTarget: PropertyReportingTarget | PaddockReportingTarget;
  reportType: "Property" | "Paddock";
  fenceLength: number;
  propertyPaddockID: string;
  showPDFModal: boolean = false;
  todayNumber: number = Date.now();






  stageCompletedTemplate = signal<StageCompletedObject>({

    1: {
      completed: false,
      data: {}
    },
    2: {
      completed: false,
      data: {}
    },
    3: {
      completed: true,
      data: {}
    },
    4: {
      completed: false,
      data: {}
    },
    5: {
      completed: false,
      data: {}
    },
    6: {
      completed: true,
      data: {}
    }
  });



  pdf = computed(() => {

    const stages = this.stageCompletedTemplate();

    const anyStageCompleted =  Object.values(stages).some(stage => stage.completed);

    if(!anyStageCompleted)
      return null;

    const payload = {
      reportTargetName: 'Test Property',
      todayDate: new Date(),
      stageData: stages
    }

    const pdf = generateFarmWoodlotPDF(payload);

    return pdf;

  });

  pdfBlob = computed(() => {
    const pdf = this.pdf();

    if(pdf == null)
      return null;

    return pdf.output('blob');
  });

  objectUrl = computed(() => {
   const blob =  this.pdfBlob();

   if(blob == null)
    return null;

   return URL.createObjectURL(blob);
  })

  safeUrl = computed(() => {

    const objectUrl = this.objectUrl();

    if(objectUrl == null)
      return '';

    return this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
  })


  // toggleAccordion method removed - Angular Primitives handles accordion state internally

  async ngOnInit(): Promise<void> {
    this.activePropertySub$ = this.reportingService.reportTarget.subscribe(
      async (reportTarget) => {
        if (reportTarget == null) {
          return;
        }
        this.reportTarget = reportTarget;
        if (reportTarget instanceof PropertyReportingTarget) {
          this.propertyPaddockID =  reportTarget['propertyID'];
          this.reportType = "Property";
        }
        else {
          this.propertyPaddockID = reportTarget['paddockID'];
          this.reportType = "Paddock";
        }

        this.fenceLength = reportTarget.fences.reduce(
          (accumulator, fence) => accumulator + fence.length,
          0
        );
      }
    );
    this.testProgrammaticPDF();


  }

  constructor()
  {


  }

setStageTemplateData(data: {
  stage: number;
  values: StageCompletedObject;
  willResetForm?: boolean;
}) {
  const stage = data.stage;
  const stageData = data.values;


  const currentTemplate = this.stageCompletedTemplate();


  const updatedStageData = {
    ...currentTemplate[stage],
    completed: data.willResetForm ? false : true,
    data: stageData
  };


  const newTemplate = {
    ...currentTemplate,
    [stage]: updatedStageData
  };


  this.stageCompletedTemplate.set(newTemplate);
}

  showWelcome() {
    this.dialog.open(FarmWoodlotToolWelcomeMessageComponent, {
      width: '1000px'
    });
  }

  generatePDFHandler() {
    const reportName = this.reportTarget
      ? this.reportTarget.name
      : 'Report name';
    const areaHa = this.reportTarget ? this.reportTarget.area : 0.0;

    // Create data structure for programmatic PDF generation
    const farmWoodlotData = {
      reportName: reportName,
      area: areaHa,
      stages: this.stageCompletedTemplate
    };

    // Generate PDF programmatically instead of using HTML
    const pdf = generateFarmWoodlotPDF(farmWoodlotData);
    pdf.save('farm-woodlot-report.pdf');
  }


  // Test method to verify programmatic PDF generation works
  testProgrammaticPDF() {
    const testData = {
      // Changed from 'reportName' to 'reportTargetName'
      reportTargetName: 'Test Property',

      // Added required 'todayDate' field
      todayDate: new Date(),

      // Changed from 'stages' to 'stageData'
      stageData: {
        1: {
          completed: true,
          data: {
            primaryUse: 'Timber Production',
            secondaryUse: 'Carbon Sequestration',
            recommendedSpecies: 'Eucalyptus globulus',
            speciesDescription:
              'Tasmanian Blue Gum - excellent for timber production',

            // Added missing 'urlInfo' field
            urlInfo: 'https://example.com/eucalyptus-globulus-info',

            recommendedPlantingLayout: 'Square',

            // Convert numbers to strings with units as expected by the PDF builder
            alongRowSpacing: '3.0m',
            betweenRowSpacing: '3.0m',

            stemsPerHectare: 1111,
            numberSeedingsRequired: 11666,
            numberTreeStocksRequired: 11666,
            amountFertiliserRequired: 500
          }
        },
        2: {
          completed: true,
          data: {
            selectedFencingType: 'Standard Fence',
            lengthOfFenceRequired: 1000,
            star: 50, // Changed from "50 posts" to number
            steelWood: 200, // Changed from "200 posts" to number

            // Fixed typo: 'standarddWire' to 'standardWire'
            standardWire: 2000, // Changed from "2000m" to number
            barbWire: 1000, // Changed from "1000m" to number
            wallabyProofMesh: 500 // Changed from "500m" to number
          }
        },
        3: {
          completed: true,
          data: {} // Stage 3 doesn't require specific data fields
        },
        4: {
          completed: true,
          data: {
            selectedCropSpecies: 'Eucalyptus globulus',

            // Convert percentage strings to numbers for area calculations
            mappedWellSuited: 6.3, // 60% of 10.5 hectares
            mappedSuitable: 3.15, // 30% of 10.5 hectares
            mappedModeratelySuitable: 1.05, // 10% of 10.5 hectares
            mappedUnsuitable: 0, // 0% of 10.5 hectares

            // Convert percentage strings to numbers for MAI estimates
            estimatedWellSuited: 25, // tonnes/ha/year for well suited areas
            estimatedSuitable: 20, // tonnes/ha/year for suitable areas
            estimatedModeratelySuitable: 15, // tonnes/ha/year for moderately suitable
            estimatedUnsuitable: 0, // tonnes/ha/year for unsuitable

            // Add missing required fields for stage 4
            totalArea: 10.5,
            averageMAI: 22.1, // Calculated weighted average
            harvestableVolume: 5775, // Calculated total volume
            harvestableVolumePerHectare: 550 // Average per hectare
          }
        },
        5: {
          completed: true,
          data: {
            // Add required fields for stage 5
            selectedMarketType: 'Domestic Timber Market',
            selectedTopic: 'Eucalyptus Timber Pricing',
            overviewDescription:
              'Current market conditions for eucalyptus timber show strong demand for construction grade lumber with prices ranging from $800-1200 per cubic meter depending on grade and processing.',
            overviewURL: 'https://example.com/timber-market-overview'
          }
        },
        6: {
          completed: true,
          data: {} // Stage 6 doesn't require specific data fields
        }
      }
    };




  }
}
